/*
  Product image zoom — replaces magnify.js + product-media-modal.
  Handles:
    - <product-lightbox>: full-screen Splide slider with per-slide zoom (wheel/dblclick + drag on desktop, pinch + double-tap + pan on mobile)
    - <product-hover-zoom>: desktop hover-zoom on the main gallery image (inline lens or side-by-side pane)
    - Click wiring on <media-gallery> to open the matching lightbox when the desktop/mobile zoom setting allows
*/
(function () {
  const MIN_SCALE = 1;
  const MAX_SCALE = 4;
  const DOUBLE_TAP_SCALE = 2.5;
  const HOVER_ZOOM_RATIO = 2.5;
  // Cache media-query results; matchMedia change listeners fire only on breakpoint crossings,
  // avoiding a window.innerWidth read on every mousemove.
  const touchQuery = window.matchMedia('(hover: none) and (pointer: coarse)');
  let _isTouchDevice = touchQuery.matches;
  touchQuery.addEventListener('change', (e) => { _isTouchDevice = e.matches; });
  const isTouchDevice = () => _isTouchDevice;

  // Hover-zoom should only ever activate on desktop-sized viewports (>= 750px),
  // regardless of input type — this matches the theme editor's mobile preview.
  const desktopQuery = window.matchMedia('(min-width: 750px)');
  let _isDesktopViewport = desktopQuery.matches;
  desktopQuery.addEventListener('change', (e) => { _isDesktopViewport = e.matches; });
  const isDesktopViewport = () => _isDesktopViewport;

  // Slide movement and zoom steps are animation: honour the OS setting.
  const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const prefersReducedMotion = () => motionQuery.matches;

  // One keyboard step of zoom, and how far one arrow press pans.
  const KEY_ZOOM_STEP = 1.4;
  const KEY_PAN_FRACTION = 0.12;

  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  const supportsInert = 'inert' in HTMLElement.prototype;


  // -------------------- ZoomState (per-slide) --------------------
  class ZoomState {
    constructor(wrap) {
      this.wrap = wrap;
      this.img = wrap.querySelector('img');
      this.scale = 1;
      this.tx = 0;
      this.ty = 0;
      this.pointers = new Map();
      this.lastTap = 0;
      this.dragging = false;
      this.dragStart = null;
      this.pinchStart = null;
      this.onWheel = this.onWheel.bind(this);
      this.onPointerDown = this.onPointerDown.bind(this);
      this.onPointerMove = this.onPointerMove.bind(this);
      this.onPointerUp = this.onPointerUp.bind(this);
      this.attach();
    }

    attach() {
      this.wrap.addEventListener('wheel', this.onWheel, { passive: false });
      this.wrap.addEventListener('pointerdown', this.onPointerDown);
      this.wrap.addEventListener('pointermove', this.onPointerMove);
      this.wrap.addEventListener('pointerup', this.onPointerUp);
      this.wrap.addEventListener('pointercancel', this.onPointerUp);
      this.wrap.addEventListener('pointerleave', this.onPointerUp);
    }

    detach() {
      this.wrap.removeEventListener('wheel', this.onWheel);
      this.wrap.removeEventListener('pointerdown', this.onPointerDown);
      this.wrap.removeEventListener('pointermove', this.onPointerMove);
      this.wrap.removeEventListener('pointerup', this.onPointerUp);
      this.wrap.removeEventListener('pointercancel', this.onPointerUp);
      this.wrap.removeEventListener('pointerleave', this.onPointerUp);
    }

    isZoomed() {
      return this.scale > 1.01;
    }

    /*
      Where the photo actually sits on screen right now.

      The wrap is the whole slide and the image fills it with
      object-fit: contain, so most of that box can be empty space beside a
      portrait photo. This narrows it to the photo, then applies the current
      zoom and pan, so 'did the shopper click the photo or the space around
      it' has one answer both zoomed in and out.
    */
    photoRect() {
      const box = this.wrap.getBoundingClientRect();
      const nw = this.img.naturalWidth;
      const nh = this.img.naturalHeight;
      if (!nw || !nh) return box;
      const fit = Math.min(box.width / nw, box.height / nh);
      let w = nw * fit;
      let h = nh * fit;
      let cx = box.left + box.width / 2;
      let cy = box.top + box.height / 2;
      if (this.scale > 1) {
        w *= this.scale;
        h *= this.scale;
        cx += this.tx;
        cy += this.ty;
      }
      return { left: cx - w / 2, top: cy - h / 2, right: cx + w / 2, bottom: cy + h / 2, width: w, height: h };
    }

    isOnPhoto(clientX, clientY) {
      const r = this.photoRect();
      return clientX >= r.left && clientX <= r.right && clientY >= r.top && clientY <= r.bottom;
    }

    apply() {
      this.clampPan();
      this.img.style.transform = `translate3d(${this.tx}px, ${this.ty}px, 0) scale(${this.scale})`;
      this.wrap.classList.toggle('is-zoomed', this.isZoomed());
      this.wrap.dispatchEvent(new CustomEvent('zoomchange', { bubbles: true, detail: { zoomed: this.isZoomed() } }));
    }

    reset() {
      this.scale = 1;
      this.tx = 0;
      this.ty = 0;
      this.apply();
    }

    /* Keyboard zoom: steps from the middle of the image, which is where
       someone without a pointer is looking. */
    zoomByStep(factor) {
      const rect = this.wrap.getBoundingClientRect();
      this.zoomAtPoint(this.scale * factor, rect.left + rect.width / 2, rect.top + rect.height / 2);
    }

    /* Keyboard pan, once zoomed in. clampPan() keeps it inside the image. */
    panBy(dirX, dirY) {
      if (!this.isZoomed()) return false;
      const rect = this.wrap.getBoundingClientRect();
      this.tx += dirX * rect.width * KEY_PAN_FRACTION;
      this.ty += dirY * rect.height * KEY_PAN_FRACTION;
      this.apply();
      return true;
    }

    clampPan() {
      // Keep image roughly within view: max pan = (scale - 1) * half-size
      const rect = this.wrap.getBoundingClientRect();
      const maxX = ((this.scale - 1) * rect.width) / 2;
      const maxY = ((this.scale - 1) * rect.height) / 2;
      this.tx = Math.max(-maxX, Math.min(maxX, this.tx));
      this.ty = Math.max(-maxY, Math.min(maxY, this.ty));
    }

    zoomAtPoint(newScale, clientX, clientY) {
      const rect = this.wrap.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      // Vector from center to cursor in image-local coords (before scaling)
      const dx = (clientX - cx - this.tx) / this.scale;
      const dy = (clientY - cy - this.ty) / this.scale;
      const clamped = Math.max(MIN_SCALE, Math.min(MAX_SCALE, newScale));
      this.tx -= dx * (clamped - this.scale);
      this.ty -= dy * (clamped - this.scale);
      this.scale = clamped;
      if (this.scale <= 1.001) {
        this.tx = 0;
        this.ty = 0;
      }
      this.apply();
    }

    onWheel(e) {
      // Only zoom on macOS pinch (ctrlKey is auto-set) or Cmd/Ctrl + wheel.
      // Plain scroll inside the lightbox should NOT zoom.
      if (!e.ctrlKey && !e.metaKey) return;
      e.preventDefault();
      const delta = -e.deltaY * 0.01;
      this.zoomAtPoint(this.scale * (1 + delta), e.clientX, e.clientY);
    }

    onPointerDown(e) {
      this.wrap.setPointerCapture(e.pointerId);
      this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

      if (this.pointers.size === 2) {
        // start pinch
        const [a, b] = Array.from(this.pointers.values());
        this.pinchStart = {
          dist: Math.hypot(a.x - b.x, a.y - b.y),
          scale: this.scale,
          centerX: (a.x + b.x) / 2,
          centerY: (a.y + b.y) / 2,
        };
        this.dragging = false;
        this.dragMoved = true; // pinch is not a click
      } else if (this.pointers.size === 1) {
        this.downStart = { x: e.clientX, y: e.clientY, tx: this.tx, ty: this.ty };
        this.dragging = false;
        this.dragMoved = false;

        // Touch double-tap detection (single tap = swipe on touch, so we wait for double)
        if (e.pointerType === 'touch') {
          const now = Date.now();
          if (now - this.lastTap < 320) {
            const target = this.isZoomed() ? 1 : DOUBLE_TAP_SCALE;
            this.zoomAtPoint(target, e.clientX, e.clientY);
            this.lastTap = 0;
            this.dragMoved = true; // suppress click-up handler
          } else {
            this.lastTap = now;
          }
        }
      }
    }

    onPointerMove(e) {
      if (!this.pointers.has(e.pointerId)) return;
      this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

      if (this.pointers.size === 2 && this.pinchStart) {
        const [a, b] = Array.from(this.pointers.values());
        const dist = Math.hypot(a.x - b.x, a.y - b.y);
        const ratio = dist / this.pinchStart.dist;
        const newScale = this.pinchStart.scale * ratio;
        this.zoomAtPoint(newScale, this.pinchStart.centerX, this.pinchStart.centerY);
        return;
      }

      if (!this.downStart) return;
      const dx = e.clientX - this.downStart.x;
      const dy = e.clientY - this.downStart.y;
      if (!this.dragMoved && Math.hypot(dx, dy) > 5) {
        this.dragMoved = true;
        if (this.isZoomed()) this.dragging = true;
      }

      if (this.dragging) {
        e.preventDefault();
        this.tx = this.downStart.tx + dx;
        this.ty = this.downStart.ty + dy;
        this.apply();
      }
    }

    onPointerUp(e) {
      // Desktop single-click toggles zoom (when pointer didn't drag and it wasn't a pinch)
      if (e.pointerType !== 'touch' && !this.dragMoved && this.downStart && this.pointers.size === 1) {
        /*
          Only when the click is on the photo. The wrap is the whole slide, so
          at normal size most of it is empty space either side of a portrait
          image - clicking there means 'close', not 'zoom in'.
        */
        if (this.isOnPhoto(e.clientX, e.clientY)) {
          const target = this.isZoomed() ? 1 : DOUBLE_TAP_SCALE;
          this.zoomAtPoint(target, e.clientX, e.clientY);
        }
      }
      this.pointers.delete(e.pointerId);
      if (this.pointers.size < 2) this.pinchStart = null;
      if (this.pointers.size === 0) {
        this.dragging = false;
        this.downStart = null;
        this.dragMoved = false;
      }
    }
  }

  // -------------------- <product-lightbox> --------------------
  class ProductLightbox extends HTMLElement {
    constructor() {
      super();
      this.splide = null;
      this.zoomStates = [];
      this.isOpen = false;
      this.onKeydown = this.onKeydown.bind(this);
      this.onZoomChange = this.onZoomChange.bind(this);

      this.querySelector('.product-lightbox__close').addEventListener('click', () => this.close());
      this.querySelector('.product-lightbox__backdrop').addEventListener('click', () => this.close());
      this.querySelector('.product-lightbox__arrow--prev').addEventListener('click', () => this.splide && this.splide.go('-1'));
      this.querySelector('.product-lightbox__arrow--next').addEventListener('click', () => this.splide && this.splide.go('+1'));

      this.dialog = this.querySelector('.product-lightbox__dialog');
      this.status = this.querySelector('.product-lightbox__status');
      this.zoomInBtn = this.querySelector('[data-zoom-in]');
      this.zoomOutBtn = this.querySelector('[data-zoom-out]');
      this.lastFocused = null;

      if (this.zoomInBtn) this.zoomInBtn.addEventListener('click', () => this.stepZoom(KEY_ZOOM_STEP));
      if (this.zoomOutBtn) this.zoomOutBtn.addEventListener('click', () => this.stepZoom(1 / KEY_ZOOM_STEP));

      this.addEventListener('zoomchange', this.onZoomChange);

      // Click away to close. pointerdown is recorded so the end of a swipe is
      // not mistaken for a click beside the photo.
      this.onDialogPointerDown = this.onDialogPointerDown.bind(this);
      this.onDialogClick = this.onDialogClick.bind(this);
      this.dialog.addEventListener('pointerdown', this.onDialogPointerDown);
      this.dialog.addEventListener('click', this.onDialogClick);
    }

    initSplideIfNeeded() {
      if (this.splide || typeof Splide === 'undefined') return;
      const container = this.querySelector('.product-lightbox__splide');
      const dotsContainer = this.querySelector('.product-lightbox__dots');
      this.splide = new Splide(container, {
        type: 'slide',
        arrows: false,
        pagination: false,
        drag: true,
        keyboard: false,
        speed: prefersReducedMotion() ? 0 : 250,
        rewind: false,
        easing: 'cubic-bezier(0.25, 1, 0.5, 1)',
      });
      this.splide.on('mounted', () => this.buildDots(dotsContainer));
      this.splide.on('moved', (newIndex) => {
        // Reset zoom on slides we leave
        this.zoomStates.forEach((z, i) => { if (i !== newIndex) z.reset(); });
        this.updateActiveDot(newIndex);
        this.announce();
      });
      // Auto-pause native videos when their slide is no longer active
      // (mirrors the pattern used by splide-component in secondary.js)
      this.splide.on('hidden', (Slide) => {
        const video = Slide.slide.querySelector('internal-video');
        if (video && video.video && typeof video.video.pause === 'function') {
          video.video.pause();
          video.classList.remove('internal-video--playing');
        }
      });
      this.splide.mount();
      // Build per-slide zoom states
      const wraps = this.querySelectorAll('[data-zoomable]');
      wraps.forEach(w => this.zoomStates.push(new ZoomState(w)));
    }

    buildDots(container) {
      if (!container || !this.splide) return;
      container.innerHTML = '';
      const total = this.splide.length;
      for (let i = 0; i < total; i++) {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'product-lightbox__dot';
        btn.setAttribute('aria-label', `Image ${i + 1} of ${total}`);
        btn.addEventListener('click', () => this.splide.go(i));
        container.appendChild(btn);
      }
      this.updateActiveDot(this.splide.index);
    }

    updateActiveDot(index) {
      this.querySelectorAll('.product-lightbox__dot').forEach((d, i) => {
        d.classList.toggle('is-active', i === index);
        // the dot is a control, so say which one is current rather than
        // leaving the state in a class name only
        if (i === index) d.setAttribute('aria-current', 'true');
        else d.removeAttribute('aria-current');
      });
    }

    currentZoom() {
      if (!this.splide) return this.zoomStates[0] || null;
      return this.zoomStates[this.splide.index] || null;
    }

    stepZoom(factor) {
      const z = this.currentZoom();
      if (!z) return;
      z.zoomByStep(factor);
      this.announce();
    }

    /* The counter is the dialog's live region: a short line on screen, a
       fuller one for a screen reader. */
    announce() {
      if (!this.status) return;
      const total = this.splide ? this.splide.length : this.querySelectorAll('.product-lightbox__slide').length;
      const index = (this.splide ? this.splide.index : 0) + 1;
      const z = this.currentZoom();
      const zoomed = z && z.isZoomed() ? `, zoomed to ${Math.round(z.scale * 100)} percent` : '';
      const visible = this.status.querySelector('.product-lightbox__count');
      const spoken = this.status.querySelector('.visually-hidden');
      if (visible) visible.textContent = `${index} / ${total}`;
      if (spoken) spoken.textContent = `Image ${index} of ${total}${zoomed}`;
      this.syncZoomButtons();
    }

    syncZoomButtons() {
      const z = this.currentZoom();
      const scale = z ? z.scale : 1;
      if (this.zoomOutBtn) this.zoomOutBtn.disabled = scale <= MIN_SCALE + 0.01;
      if (this.zoomInBtn) this.zoomInBtn.disabled = scale >= MAX_SCALE - 0.01;
    }

    /* While the dialog is open the rest of the page is inert, so neither Tab
       nor a screen reader's own cursor can wander into it. */
    setBackgroundInert(on) {
      if (!supportsInert) return;
      if (on) {
        this.inertedElements = [];
        let node = this;
        while (node && node !== document.body) {
          const parent = node.parentElement;
          if (!parent) break;
          for (const sibling of parent.children) {
            if (sibling !== node && !sibling.inert) {
              sibling.inert = true;
              this.inertedElements.push(sibling);
            }
          }
          node = parent;
        }
      } else {
        (this.inertedElements || []).forEach(el => { el.inert = false; });
        this.inertedElements = [];
      }
    }

    trapFocus(e) {
      const items = [...this.querySelectorAll(FOCUSABLE)].filter(el => el.offsetWidth || el.offsetHeight || el === document.activeElement);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && (document.activeElement === first || !this.contains(document.activeElement))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      } else if (!this.contains(document.activeElement)) {
        e.preventDefault();
        first.focus();
      }
    }

    onDialogPointerDown(e) {
      this.pointerDownAt = { x: e.clientX, y: e.clientY };
      /*
        The image's own pointerup runs before this click handler and changes the
        zoom, which would move the photo out from under the click. So the
        question 'was this on the photo' is answered here, on the way down.
      */
      const z = this.currentZoom();
      this.downOnPhoto = !!(z && z.isOnPhoto(e.clientX, e.clientY));
    }

    onDialogClick(e) {
      // Controls look after themselves.
      if (e.target.closest('button, a, [role="button"]')) return;

      // A swipe ends in a click event too; 8px of travel is enough to tell
      // the two apart.
      if (this.pointerDownAt) {
        const moved = Math.hypot(e.clientX - this.pointerDownAt.x, e.clientY - this.pointerDownAt.y);
        this.pointerDownAt = null;
        if (moved > 8) return;
      }

      // On the photo the click belongs to the image: it zooms in, or back out.
      if (this.downOnPhoto) return;

      // Video and 3D slides play in place; don't close from inside them.
      if (e.target.closest('.product-lightbox__media-non-image')) return;

      this.close();
    }

    onZoomChange(e) {
      // When any slide is zoomed, disable Splide drag (so swipe pans instead of changing slide)
      if (!this.splide) return;
      const anyZoomed = this.zoomStates.some(z => z.isZoomed());
      this.splide.options = { drag: !anyZoomed };
      this.classList.toggle('is-slide-zoomed', anyZoomed);
    }

    open(targetMediaId) {
      // Remember where the shopper was, so closing puts them back there.
      this.lastFocused = document.activeElement;
      this.hidden = false;
      document.body.classList.add('overflow-hidden');
      this.setBackgroundInert(true);
      // Mount splide after the element is visible so it can measure sizes correctly
      requestAnimationFrame(() => {
        this.initSplideIfNeeded();
        if (this.splide && targetMediaId) {
          const slides = this.querySelectorAll('.product-lightbox__slide');
          let index = 0;
          slides.forEach((s, i) => { if (s.dataset.mediaId === targetMediaId) index = i; });
          this.splide.go(index);
        } else if (this.splide) {
          this.splide.refresh();
        }
      });
      this.isOpen = true;
      document.addEventListener('keydown', this.onKeydown);
      // Move focus into the dialog: the close button is the safe first stop.
      requestAnimationFrame(() => {
        const close = this.querySelector('.product-lightbox__close');
        if (close) close.focus();
        this.announce();
      });
    }

    close() {
      this.hidden = true;
      this.isOpen = false;
      document.body.classList.remove('overflow-hidden');
      this.zoomStates.forEach(z => z.reset());
      document.removeEventListener('keydown', this.onKeydown);
      this.setBackgroundInert(false);
      // Back to the control that opened it.
      if (this.lastFocused && typeof this.lastFocused.focus === 'function' && document.contains(this.lastFocused)) {
        this.lastFocused.focus();
      }
      this.lastFocused = null;
    }

    onKeydown(e) {
      if (!this.isOpen) return;
      const zoom = this.currentZoom();

      if (e.key === 'Escape') {
        this.close();
        return;
      }

      if (e.key === 'Tab') {
        this.trapFocus(e);
        return;
      }

      // Plus, minus and zero: the shortcuts people already know from maps and
      // image viewers. Shift+= is what a plus key reports on many layouts.
      if (e.key === '+' || e.key === '=') {
        e.preventDefault();
        this.stepZoom(KEY_ZOOM_STEP);
        return;
      }
      if (e.key === '-' || e.key === '_') {
        e.preventDefault();
        this.stepZoom(1 / KEY_ZOOM_STEP);
        return;
      }
      if (e.key === '0') {
        e.preventDefault();
        if (zoom) zoom.reset();
        this.announce();
        return;
      }

      // Zoomed in, the arrows pan the image; at normal size they change image.
      const pans = {
        ArrowLeft: [1, 0], ArrowRight: [-1, 0], ArrowUp: [0, 1], ArrowDown: [0, -1],
      };
      if (pans[e.key] && zoom && zoom.isZoomed()) {
        e.preventDefault();
        zoom.panBy(pans[e.key][0], pans[e.key][1]);
        return;
      }
      if (e.key === 'ArrowLeft' && this.splide) {
        this.splide.go('-1');
      } else if (e.key === 'ArrowRight' && this.splide) {
        this.splide.go('+1');
      }
    }
  }
  if (!customElements.get('product-lightbox')) customElements.define('product-lightbox', ProductLightbox);

  // -------------------- <product-hover-zoom> --------------------
  class ProductHoverZoom extends HTMLElement {
    constructor() {
      super();
      this.style_ = this.dataset.style || 'inline'; // 'inline' or 'pane'
      this.image = this.querySelector('img');
      this.lens = null;
      this.pane = null;
      this.active = false;

      if (!this.image) return;
      this.onMove = this.onMove.bind(this);
      this.onEnter = this.onEnter.bind(this);
      this.onLeave = this.onLeave.bind(this);

      this.addEventListener('mouseenter', this.onEnter);
      this.addEventListener('mousemove', this.onMove);
      this.addEventListener('mouseleave', this.onLeave);
    }

    onEnter() {
      if (isTouchDevice() || !isDesktopViewport()) return;
      this.active = true;
      if (this.style_ === 'pane') this.setupPane();
      else this.setupInline();
    }

    setupInline() {
      this.classList.add('product-hover-zoom--inline-active');
      this.image.style.transformOrigin = '50% 50%';
      this.image.style.transition = 'transform 0.05s linear';
    }

    setupPane() {
      if (this.pane) return;
      this.pane = document.createElement('div');
      this.pane.className = 'product-hover-zoom__pane';
      this.pane.style.display = 'block';
      this.pane.style.backgroundImage = `url('${this.image.currentSrc || this.image.src}')`;
      document.body.appendChild(this.pane);

      const rect = this.getBoundingClientRect();
      const gap = 16;
      const PANE_RATIO = 0.75;
      const paneW = rect.width * PANE_RATIO;
      const paneH = rect.height * PANE_RATIO;
      // Top-align with the image (industry standard).
      const paneTop = rect.top;
      // Prefer right side; fall back to left if no room.
      const rightFits = rect.right + gap + paneW <= window.innerWidth;
      const leftX = rightFits ? rect.right + gap : Math.max(8, rect.left - gap - paneW);
      this.pane.style.top = paneTop + 'px';
      this.pane.style.left = leftX + 'px';
      this.pane.style.width = paneW + 'px';
      this.pane.style.height = paneH + 'px';

      // Loupe: visual indicator on the source showing which area is magnified.
      // Size = source area visible in pane = rect / HOVER_ZOOM_RATIO (independent of pane size).
      this.loupe = document.createElement('div');
      this.loupe.className = 'product-hover-zoom__loupe';
      this.loupe.style.display = 'block';
      this.loupe.style.width = (rect.width / HOVER_ZOOM_RATIO) + 'px';
      this.loupe.style.height = (rect.height / HOVER_ZOOM_RATIO) + 'px';
      this.appendChild(this.loupe);

      this.classList.add('product-hover-zoom--pane-active');
    }

    onMove(e) {
      if (!this.active || isTouchDevice() || !isDesktopViewport()) return;
      const rect = this.getBoundingClientRect();
      const xPct = ((e.clientX - rect.left) / rect.width) * 100;
      const yPct = ((e.clientY - rect.top) / rect.height) * 100;
      if (this.style_ === 'pane' && this.pane) {
        this.pane.style.backgroundSize = `${HOVER_ZOOM_RATIO * 100}%`;
        this.pane.style.backgroundPosition = `${xPct}% ${yPct}%`;
        if (this.loupe) {
          const loupeW = this.loupe.offsetWidth;
          const loupeH = this.loupe.offsetHeight;
          let lx = (e.clientX - rect.left) - loupeW / 2;
          let ly = (e.clientY - rect.top) - loupeH / 2;
          lx = Math.max(0, Math.min(rect.width - loupeW, lx));
          ly = Math.max(0, Math.min(rect.height - loupeH, ly));
          this.loupe.style.left = lx + 'px';
          this.loupe.style.top = ly + 'px';
        }
      } else {
        this.image.style.transformOrigin = `${xPct}% ${yPct}%`;
        this.image.style.transform = `scale(${HOVER_ZOOM_RATIO})`;
      }
    }

    onLeave() {
      this.active = false;
      this.classList.remove('product-hover-zoom--inline-active', 'product-hover-zoom--pane-active');
      if (this.image) {
        this.image.style.transform = '';
        this.image.style.transformOrigin = '';
        this.image.style.transition = '';
      }
      if (this.pane) {
        this.pane.remove();
        this.pane = null;
      }
      if (this.loupe) {
        this.loupe.remove();
        this.loupe = null;
      }
    }
  }
  if (!customElements.get('product-hover-zoom')) customElements.define('product-hover-zoom', ProductHoverZoom);

  // -------------------- Wire <media-gallery> click -> lightbox open --------------------
  function wireGalleryLightbox(gallery) {
    const sectionId = gallery.dataset.section;
    if (!sectionId) return;
    const lightbox = document.getElementById(`ProductLightbox-${sectionId}`);
    if (!lightbox) return;

    const desktopAllows = ['lightbox', 'combined'].includes(gallery.dataset.zoomDesktop);
    const mobileAllows = gallery.dataset.zoomMobile === 'lightbox';

    /*
      The only way in used to be a click on the image, which no keyboard can
      reach. This puts a real button on each image, over the magnifier badge
      the theme already draws, and keeps the set in step with the zoom setting
      for the current breakpoint.
    */
    const syncOpeners = () => {
      const allowed = isDesktopViewport() ? desktopAllows : mobileAllows;
      const images = [...gallery.querySelectorAll('.product__media-item')]
        .filter(s => s.querySelector('img') && !s.classList.contains('product__media-item--full'));

      images.forEach((slide, i) => {
        const opener = slide.querySelector('.product__modal-opener');
        let btn = slide.querySelector('.eco-zoom-open');
        if (!allowed || !opener) {
          if (btn) btn.remove();
          return;
        }
        if (!btn) {
          btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'eco-zoom-open';
          btn.setAttribute('aria-haspopup', 'dialog');
          btn.addEventListener('click', (ev) => {
            // the gallery's own click handler would otherwise open it twice
            ev.stopPropagation();
            lightbox.open(slide.dataset.mediaId);
          });
          opener.appendChild(btn);
        }
        btn.setAttribute('aria-label', `Zoom image ${i + 1} of ${images.length}`);
      });
    };

    syncOpeners();
    desktopQuery.addEventListener('change', syncOpeners);

    gallery.addEventListener('click', (e) => {
      const slide = e.target.closest('.product__media-item');
      if (!slide) return;
      /*
        The gallery used to require .is-active here. The theme only moves that
        class when a thumbnail is used, not when the gallery is swiped or the
        arrows are pressed, so on a phone every image after the first one
        ignored the tap and the zoom never opened.

        The tapped slide is trusted instead, as long as at least half of it is
        inside the gallery's own viewport - that is the image the shopper is
        looking at.
      */
      const galleryBox = gallery.getBoundingClientRect();
      const slideBox = slide.getBoundingClientRect();
      const onScreen = Math.min(galleryBox.right, slideBox.right) - Math.max(galleryBox.left, slideBox.left);
      if (!slideBox.width || onScreen < slideBox.width / 2) return;
      // Skip non-image media (video, model) — those play in place
      const isImage = slide.querySelector('img') && !slide.classList.contains('product__media-item--full');
      if (!isImage) return;
      const allowed = isDesktopViewport() ? desktopAllows : mobileAllows;
      if (!allowed) return;
      const mediaId = slide.dataset.mediaId;
      lightbox.open(mediaId);
    });
  }

  function init() {
    document.querySelectorAll('media-gallery[data-zoom-desktop], media-gallery[data-zoom-mobile]').forEach(wireGalleryLightbox);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
